/**
 * Price Forecasting Agent
 * Predicts price trends for seasonal produce based on supply, demand, and market conditions
 */

interface PriceForecast {
  product: string;
  currentPrice: { min: number; max: number; average: number };
  predictedPrice: {
    nextWeek: { min: number; max: number; average: number };
    nextMonth: { min: number; max: number; average: number };
  };
  trend: 'increasing' | 'stable' | 'decreasing';
  confidence: number;
  factors: string[];
  recommendation: string;
}

interface MarketConditions {
  supply: 'abundant' | 'normal' | 'limited';
  demand: 'high' | 'normal' | 'low';
  seasonality: 'peak' | 'shoulder' | 'off-season';
  weatherImpact: 'positive' | 'neutral' | 'negative';
}

interface PriceComparison {
  product: string;
  prices: Array<{
    farmName: string;
    farmId: string;
    price: number;
    unit: string;
    quality: 'premium' | 'standard' | 'value';
  }>;
  average: number;
  median: number;
  bestValue: string;
}

class PriceForecastingAgent {
  private priceHistory: Map<string, number[]>;
  private baselinePrices: Map<string, number>;

  constructor() {
    this.priceHistory = new Map();
    this.baselinePrices = this.initializeBaselinePrices();
  }

  /**
   * Forecast price trends for a product
   * @param product Product name
   * @param currentConditions Market conditions
   * @returns Price forecast
   */
  forecastPrice(product: string, currentConditions?: MarketConditions): PriceForecast {
    const baseline = this.baselinePrices.get(product.toLowerCase()) || 3.0;
    const conditions = currentConditions || this.estimateMarketConditions(product);

    // Calculate current price based on conditions
    const currentPrice = this.calculateCurrentPrice(baseline, conditions);

    // Predict future prices
    const nextWeek = this.predictShortTerm(baseline, conditions);
    const nextMonth = this.predictLongTerm(baseline, conditions);

    // Determine trend
    const trend = this.determineTrend(currentPrice.average, nextWeek.average, nextMonth.average);

    // Calculate confidence
    const confidence = this.calculateConfidence(product, conditions);

    // Identify price factors
    const factors = this.identifyPriceFactors(conditions, trend);

    // Generate recommendation
    const recommendation = this.generatePriceRecommendation(trend, conditions);

    return {
      product,
      currentPrice,
      predictedPrice: { nextWeek, nextMonth },
      trend,
      confidence,
      factors,
      recommendation,
    };
  }

  /**
   * Compare prices across farms
   * @param product Product name
   * @param farmPrices Array of farm prices
   * @returns Price comparison
   */
  comparePrices(product: string, farmPrices: PriceComparison['prices']): PriceComparison {
    if (farmPrices.length === 0) {
      return {
        product,
        prices: [],
        average: 0,
        median: 0,
        bestValue: '',
      };
    }

    const prices = farmPrices.map(p => p.price).sort((a, b) => a - b);
    const average = prices.reduce((sum, p) => sum + p, 0) / prices.length;
    const median = prices[Math.floor(prices.length / 2)];

    // Find best value (considering quality)
    const valueScores = farmPrices.map(farm => ({
      ...farm,
      valueScore: this.calculateValueScore(farm.price, farm.quality, average),
    }));

    const bestValue = valueScores.sort((a, b) => b.valueScore - a.valueScore)[0];

    return {
      product,
      prices: farmPrices,
      average: Math.round(average * 100) / 100,
      median: Math.round(median * 100) / 100,
      bestValue: bestValue.farmName,
    };
  }

  /**
   * Get historical price trends
   * @param product Product name
   * @param months Number of months to analyze
   * @returns Price trend data
   */
  getHistoricalTrends(product: string, months: number = 12): {
    product: string;
    data: Array<{ month: string; avgPrice: number }>;
    seasonalPattern: string;
  } {
    const history = this.priceHistory.get(product.toLowerCase()) || [];
    const baseline = this.baselinePrices.get(product.toLowerCase()) || 3.0;

    // Generate monthly data (simulated for now)
    const data: Array<{ month: string; avgPrice: number }> = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    for (let i = 0; i < months; i++) {
      const monthIndex = (new Date().getMonth() - months + i + 12) % 12;
      const seasonalFactor = this.getSeasonalPriceFactor(product, monthIndex);
      const price = baseline * seasonalFactor;

      data.push({
        month: monthNames[monthIndex],
        avgPrice: Math.round(price * 100) / 100,
      });
    }

    const seasonalPattern = this.identifySeasonalPattern(data);

    return { product, data, seasonalPattern };
  }

  /**
   * Get budget-friendly product alternatives
   * @param product Product name
   * @param maxPrice Maximum price willing to pay
   * @returns Alternative product suggestions
   */
  getBudgetAlternatives(product: string, maxPrice: number): Array<{
    product: string;
    price: number;
    savings: number;
    similarity: number;
  }> {
    const alternatives: Array<any> = [];
    const productCategories: Record<string, string[]> = {
      tomatoes: ['cherry tomatoes', 'roma tomatoes', 'heirloom tomatoes'],
      lettuce: ['romaine', 'butterhead', 'mixed greens', 'spinach'],
      berries: ['strawberries', 'blueberries', 'blackberries'],
    };

    const category = Object.keys(productCategories).find(cat =>
      product.toLowerCase().includes(cat) || productCategories[cat].includes(product.toLowerCase())
    );

    if (category) {
      for (const alt of productCategories[category]) {
        const altPrice = this.baselinePrices.get(alt.toLowerCase()) || 3.0;
        if (altPrice <= maxPrice) {
          alternatives.push({
            product: alt,
            price: altPrice,
            savings: Math.round((maxPrice - altPrice) * 100) / 100,
            similarity: this.calculateSimilarity(product, alt),
          });
        }
      }
    }

    return alternatives.sort((a, b) => b.similarity - a.similarity);
  }

  // Helper Methods

  private initializeBaselinePrices(): Map<string, number> {
    const prices = new Map<string, number>();

    // Baseline prices per pound
    prices.set('tomatoes', 3.50);
    prices.set('strawberries', 5.00);
    prices.set('lettuce', 2.50);
    prices.set('peppers', 4.00);
    prices.set('cucumbers', 2.00);
    prices.set('blueberries', 6.00);
    prices.set('squash', 2.50);
    prices.set('beans', 3.00);
    prices.set('corn', 1.50);
    prices.set('herbs', 4.50);

    return prices;
  }

  private estimateMarketConditions(product: string): MarketConditions {
    const month = new Date().getMonth();
    const seasonality = this.determineSeasonality(product, month);

    return {
      supply: seasonality === 'peak' ? 'abundant' : seasonality === 'off-season' ? 'limited' : 'normal',
      demand: 'normal',
      seasonality,
      weatherImpact: 'neutral',
    };
  }

  private determineSeasonality(product: string, month: number): MarketConditions['seasonality'] {
    // Simplified seasonality logic
    const peakSeasons: Record<string, number[]> = {
      strawberries: [0, 1, 2],
      tomatoes: [2, 3, 4, 10, 11],
      lettuce: [10, 11, 0, 1, 2],
    };

    const peaks = peakSeasons[product.toLowerCase()] || [2, 3, 4];
    if (peaks.includes(month)) return 'peak';

    const shoulder = peaks.map(m => [(m - 1 + 12) % 12, (m + 1) % 12]).flat();
    if (shoulder.includes(month)) return 'shoulder';

    return 'off-season';
  }

  private calculateCurrentPrice(
    baseline: number,
    conditions: MarketConditions
  ): { min: number; max: number; average: number } {
    let multiplier = 1.0;

    // Supply impact
    if (conditions.supply === 'abundant') multiplier *= 0.8;
    if (conditions.supply === 'limited') multiplier *= 1.3;

    // Demand impact
    if (conditions.demand === 'high') multiplier *= 1.2;
    if (conditions.demand === 'low') multiplier *= 0.9;

    // Weather impact
    if (conditions.weatherImpact === 'negative') multiplier *= 1.15;
    if (conditions.weatherImpact === 'positive') multiplier *= 0.95;

    const average = baseline * multiplier;
    const min = average * 0.85;
    const max = average * 1.15;

    return {
      min: Math.round(min * 100) / 100,
      max: Math.round(max * 100) / 100,
      average: Math.round(average * 100) / 100,
    };
  }

  private predictShortTerm(
    baseline: number,
    conditions: MarketConditions
  ): { min: number; max: number; average: number } {
    // Slight adjustment from current
    const current = this.calculateCurrentPrice(baseline, conditions);
    const variability = 0.05;

    return {
      min: Math.round(current.min * (1 - variability) * 100) / 100,
      max: Math.round(current.max * (1 + variability) * 100) / 100,
      average: Math.round(current.average * (1 + (Math.random() * variability * 2 - variability)) * 100) / 100,
    };
  }

  private predictLongTerm(
    baseline: number,
    conditions: MarketConditions
  ): { min: number; max: number; average: number } {
    // Seasonal transition
    let multiplier = 1.0;
    if (conditions.seasonality === 'peak') multiplier = 1.1; // Moving out of peak
    if (conditions.seasonality === 'off-season') multiplier = 0.95; // Moving toward season

    const average = baseline * multiplier;
    return {
      min: Math.round(average * 0.85 * 100) / 100,
      max: Math.round(average * 1.15 * 100) / 100,
      average: Math.round(average * 100) / 100,
    };
  }

  private determineTrend(current: number, nextWeek: number, nextMonth: number): PriceForecast['trend'] {
    const weekChange = ((nextWeek - current) / current) * 100;
    const monthChange = ((nextMonth - current) / current) * 100;

    if (weekChange > 3 || monthChange > 5) return 'increasing';
    if (weekChange < -3 || monthChange < -5) return 'decreasing';
    return 'stable';
  }

  private calculateConfidence(product: string, conditions: MarketConditions): number {
    let confidence = 70; // Base confidence

    if (conditions.seasonality === 'peak') confidence += 15;
    if (conditions.weatherImpact === 'neutral') confidence += 10;
    if (this.priceHistory.has(product.toLowerCase())) confidence += 5;

    return Math.min(95, confidence);
  }

  private identifyPriceFactors(conditions: MarketConditions, trend: PriceForecast['trend']): string[] {
    const factors: string[] = [];

    if (conditions.supply === 'abundant') factors.push('High supply keeping prices low');
    if (conditions.supply === 'limited') factors.push('Limited supply driving prices up');
    if (conditions.demand === 'high') factors.push('Strong demand increasing prices');
    if (conditions.seasonality === 'peak') factors.push('Peak season availability');
    if (conditions.seasonality === 'off-season') factors.push('Off-season premiums');
    if (conditions.weatherImpact === 'negative') factors.push('Weather conditions affecting crops');

    if (trend === 'increasing') factors.push('Prices trending upward');
    if (trend === 'decreasing') factors.push('Prices expected to drop soon');

    return factors;
  }

  private generatePriceRecommendation(trend: PriceForecast['trend'], conditions: MarketConditions): string {
    if (trend === 'increasing' && conditions.supply === 'limited') {
      return 'Buy now before prices increase further';
    }
    if (trend === 'decreasing') {
      return 'Wait a week or two for better prices';
    }
    if (conditions.seasonality === 'peak' && trend === 'stable') {
      return 'Great time to buy - peak season with stable prices';
    }
    return 'Fair pricing - buy as needed';
  }

  private calculateValueScore(price: number, quality: PriceComparison['prices'][0]['quality'], average: number): number {
    let score = 50;

    // Price vs average
    const priceDiff = ((average - price) / average) * 100;
    score += priceDiff * 0.5;

    // Quality bonus
    if (quality === 'premium') score += 20;
    if (quality === 'standard') score += 10;

    return score;
  }

  private getSeasonalPriceFactor(product: string, month: number): number {
    const seasonality = this.determineSeasonality(product, month);
    if (seasonality === 'peak') return 0.85;
    if (seasonality === 'shoulder') return 1.0;
    return 1.3;
  }

  private identifySeasonalPattern(data: Array<{ month: string; avgPrice: number }>): string {
    const prices = data.map(d => d.avgPrice);
    const avg = prices.reduce((sum, p) => sum + p, 0) / prices.length;
    const variance = prices.reduce((sum, p) => sum + Math.pow(p - avg, 2), 0) / prices.length;

    if (variance < avg * 0.1) return 'Stable year-round pricing';
    if (variance > avg * 0.3) return 'High seasonal price variation';
    return 'Moderate seasonal price fluctuation';
  }

  private calculateSimilarity(product1: string, product2: string): number {
    // Simple similarity based on name matching
    const p1 = product1.toLowerCase();
    const p2 = product2.toLowerCase();

    if (p1 === p2) return 100;
    if (p1.includes(p2) || p2.includes(p1)) return 80;

    // Check if same category
    const categories: Record<string, string[]> = {
      tomatoes: ['tomatoes', 'cherry tomatoes', 'roma tomatoes'],
      lettuce: ['lettuce', 'romaine', 'butterhead'],
    };

    for (const [_, items] of Object.entries(categories)) {
      if (items.includes(p1) && items.includes(p2)) return 70;
    }

    return 50;
  }
}

export const priceForecastingAgent = new PriceForecastingAgent();
export default priceForecastingAgent;
